import { useEffect, useMemo, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";
import { fieldOptionMultipleRetrieve } from "utils/common";

import Box from "components/Box";
import Stack from "components/Stack";
import Text from "components/Text";

import PrintableMemoFieldTitle from "pages/Deal/PrintableMemo/components/PrintableMemoFieldTitle";

import { IPackageDetailsField } from "entities/Checklist/sdk";
import { IFieldOption } from "entities/Package/sdk";

interface IPrintableCheckboxfield {
  field: IPackageDetailsField;
  isNASection: boolean;
}

const PrintableCheckboxfield = ({
  field,
  isNASection,
}: IPrintableCheckboxfield) => {
  const [value, setValue] = useState<Array<IFieldOption> | null>(
    fieldOptionMultipleRetrieve({
      options: field.options,
      value: _.get(field.values, "[0].selected_value"),
    })
  );

  useEffect(() => {
    setValue(
      fieldOptionMultipleRetrieve({
        options: field.options,
        value: _.get(field.values, "[0].selected_value"),
      })
    );
  }, [field.options, field.values]);

  const isNAField = useMemo(
    () => field.n_a_status || isNASection,
    [field, isNASection]
  );

  const showMissingTag = useMemo(
    () => !isNAField && (_.isEmpty(value) || _.isNil(value)),
    [isNAField, value]
  );

  const displayValue = useMemo(() => {
    if (isNAField) {
      return null;
    }

    if (value?.length === 1) {
      return <Box data-testid="field-value">{value[0].label}</Box>;
    }

    return (
      <ul
        data-testid="field-value"
        style={{
          margin: 0,
        }}
      >
        {value
          ?.map((v) => v.label)
          .map((item) => (
            <li>
              <Text variant="text2">{item}</Text>
            </li>
          ))}
      </ul>
    );
  }, [value, isNAField]);

  return (
    <Stack
      sx={{
        borderBottom: `1px solid ${colors.blue40}`,
      }}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <PrintableMemoFieldTitle field={field} disabled={isNAField} />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flex: 1 }}
      >
        {!showMissingTag && displayValue}

        {isNAField && (
          <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
            N/A
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

export default PrintableCheckboxfield;
